import type { Grade } from "./types";

/**
 * Annual stream grades, exactly as the Airtable base computes them:
 *
 *   Total Score = Taxa score + EPT family score + P-sensitive score  (max 100)
 *   A >= 70   B >= 50   C >= 30   D >= 20   E >= 10   F < 10
 *
 * The grade letter is the primary encoding and is always shown; colour is a
 * secondary cue, so the scale does not have to carry the meaning alone.
 */
export const GRADES: Record<Grade, {
  label: string; color: string; soft: string; blurb: string;
}> = {
  A: { label: "A", color: "#1A7A3C", soft: "#E7F3EA",
       blurb: "Excellent — a diverse community with many sensitive organisms." },
  B: { label: "B", color: "#6FA12E", soft: "#EEF4E4",
       blurb: "Good — a healthy community with sensitive organisms present." },
  C: { label: "C", color: "#C99700", soft: "#FBF3DF",
       blurb: "Fair — a moderate community, with sensitive organisms reduced." },
  D: { label: "D", color: "#E07B18", soft: "#FDF0E3",
       blurb: "Poor — low diversity and few sensitive organisms." },
  E: { label: "E", color: "#C2410C", soft: "#FBEBE3",
       blurb: "Very poor — very low diversity and sensitive organisms largely absent." },
  F: { label: "F", color: "#A4161A", soft: "#FAE9EA",
       blurb: "Severely impaired — almost no diversity and no sensitive organisms." },
  none: { label: "—", color: "#8A93A6", soft: "#F0F2F6",
          blurb: "No scored collection on record for this site." },
};

export const GRADE_ORDER: Grade[] = ["A", "B", "C", "D", "E", "F", "none"];

/** Score cut-points, used both to grade and to draw the chart bands. */
export function gradeFor(score: number | null | undefined): Grade {
  if (score === null || score === undefined || !Number.isFinite(score)) return "none";
  if (score >= 70) return "A";
  if (score >= 50) return "B";
  if (score >= 30) return "C";
  if (score >= 20) return "D";
  if (score >= 10) return "E";
  return "F";
}

/** Background bands for the Total Score chart. Drawn bottom-up. */
export const SCORE_BANDS = [
  { from: 0,  to: 10,  color: GRADES.F.soft, label: "F" },
  { from: 10, to: 20,  color: GRADES.E.soft, label: "E" },
  { from: 20, to: 30,  color: GRADES.D.soft, label: "D" },
  { from: 30, to: 50,  color: GRADES.C.soft, label: "C" },
  { from: 50, to: 70,  color: GRADES.B.soft, label: "B" },
  { from: 70, to: 100, color: GRADES.A.soft, label: "A" },
];
